import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getNoticeBoard,
    acknowledgeNotice,
    getNoticeEnums,
} from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_NOTICES, SOCIETY_PORTAL_NOTICES_VIEW } from '../../../Routing/routes';
import {
    NOTICE_TYPE_STYLE,
    NOTICE_TYPE_ICON,
    PRIORITY_STYLE,
    CATEGORY_STYLE,
    CATEGORY_ICON,
    FALLBACK_ENUMS,
    humanize,
    fmtDateTime,
    excerpt,
    authorName,
} from './noticeConstants';

// Read-only feed of everything currently published to the signed-in user.
//
// This is the screen residents and staff live in — no drafts, no edit controls,
// no per-row admin actions. The only interaction is acknowledging a notice that
// asks for it. The backend already filters by audience and expiry, so anything
// that arrives here is meant to be shown.
const NoticeBoard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        noticeBoardData,
        noticeBoardLoading,
        noticeBoardPagination,
        acknowledgeLoading,
        noticeEnumsData,
    } = useSelector(state => state.NoticeReducer || {});

    const [search, setSearch] = useState('');
    const [noticeType, setNoticeType] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 12;

    const fetchBoard = useCallback(() => {
        if (!societyId) return;
        dispatch(getNoticeBoard({
            societyId,
            page,
            limit: itemsPerPage,
            search: search || undefined,
            noticeType: noticeType || undefined,
            category: category || undefined,
        }));
    }, [dispatch, societyId, page, search, noticeType, category]);
    usePageRefresh(fetchBoard);

    useEffect(() => { fetchBoard(); }, [fetchBoard]);
    useEffect(() => {
        if (!noticeEnumsData) dispatch(getNoticeEnums());
    }, [dispatch, noticeEnumsData]);

    const rows = useMemo(
        () => (Array.isArray(noticeBoardData) ? noticeBoardData : []),
        [noticeBoardData]
    );

    // Pinned items lead regardless of the page's sort so urgent standing notices
    // stay visible as the resident pages through the rest.
    const ordered = useMemo(() => {
        const pinned = rows.filter(n => n.is_pinned);
        const rest = rows.filter(n => !n.is_pinned);
        return [...pinned, ...rest];
    }, [rows]);

    const totalPages = noticeBoardPagination?.totalPages || 1;
    const totalItems = noticeBoardPagination?.total || rows.length;
    const categories = noticeEnumsData?.categories || FALLBACK_ENUMS.categories;

    const unread = rows.filter(n => n.requires_acknowledgement && !n.acknowledged_by_me).length;

    const openNotice = (id) => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_VIEW.replace(':id', id));

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:bulletin-board" className="tw-text-sky-600" />
                            Notice Board
                        </h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Notices and circulars currently active for your society
                        </p>
                    </div>
                    {unread > 0 && (
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-amber-50 tw-text-amber-800 tw-border tw-border-amber-100">
                            <Icon icon="mdi:alert-circle-outline" className="tw-text-sm" />
                            {unread} awaiting your acknowledgement
                        </span>
                    )}
                </div>

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                    <div className="sm:tw-col-span-2 tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search notices..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                        />
                    </div>
                    <select
                        value={noticeType}
                        onChange={e => { setNoticeType(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">Notices &amp; Circulars</option>
                        <option value="NOTICE">Notices only</option>
                        <option value="CIRCULAR">Circulars only</option>
                    </select>
                    <select
                        value={category}
                        onChange={e => { setCategory(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{humanize(c)}</option>)}
                    </select>
                </div>

                <div className="tw-p-4">
                    {noticeBoardLoading && (
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500"></div>
                            <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                        </div>
                    )}
                    {!noticeBoardLoading && ordered.length === 0 && (
                        <div className="tw-text-center tw-py-14">
                            <Icon icon="mdi:bulletin-board" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Nothing on the board right now</p>
                            {/* The board carries published, unexpired notices only.
                                A notice saved as a draft never reaches it, which
                                reads as "my notice disappeared". */}
                            <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1">
                                Only published notices that have not expired appear here.{' '}
                                <Link
                                    to={SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES}
                                    className="tw-text-sky-600 hover:tw-underline"
                                >
                                    Manage notices
                                </Link>{' '}
                                to publish a draft.
                            </p>
                        </div>
                    )}
                    {!noticeBoardLoading && ordered.length > 0 && (
                        <div className="tw-space-y-3">
                            {ordered.map(n => {
                                const id = n.notice_id;
                                const needsAck = n.requires_acknowledgement && !n.acknowledged_by_me;
                                return (
                                    <article
                                        key={id}
                                        className={`tw-border tw-rounded-lg tw-p-4 tw-transition-shadow hover:tw-shadow-sm ${n.is_pinned ? 'tw-border-amber-200 tw-bg-amber-50/40' : 'tw-border-gray-100'
                                            }`}
                                    >
                                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-mb-1.5">
                                            {n.is_pinned && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-bg-amber-100 tw-text-amber-700">
                                                    <Icon icon="mdi:pin" className="tw-text-xs" /> Pinned
                                                </span>
                                            )}
                                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${NOTICE_TYPE_STYLE[n.notice_type] || NOTICE_TYPE_STYLE.NOTICE}`}>
                                                <Icon icon={NOTICE_TYPE_ICON[n.notice_type] || NOTICE_TYPE_ICON.NOTICE} className="tw-text-xs" />
                                                {humanize(n.notice_type)}
                                            </span>
                                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${CATEGORY_STYLE[n.category] || CATEGORY_STYLE.GENERAL}`}>
                                                <Icon icon={CATEGORY_ICON[n.category] || CATEGORY_ICON.GENERAL} className="tw-text-xs" />
                                                {humanize(n.category)}
                                            </span>
                                            {(n.priority === 'HIGH' || n.priority === 'URGENT') && (
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${PRIORITY_STYLE[n.priority]}`}>
                                                    {humanize(n.priority)}
                                                </span>
                                            )}
                                            {needsAck && (
                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-bg-rose-50 tw-text-rose-700">
                                                    <Icon icon="mdi:alert-circle-outline" className="tw-text-xs" /> Action needed
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => openNotice(id)}
                                            className="tw-text-left tw-w-full"
                                        >
                                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 hover:tw-text-sky-700 tw-transition-colors">
                                                {n.title}
                                            </h2>
                                        </button>
                                        <p className="tw-text-xs tw-text-gray-600 tw-mt-1">
                                            {n.summary || excerpt(n.body, 220)}
                                        </p>

                                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-3 tw-pt-2 tw-border-t tw-border-gray-100 tw-text-[11px] tw-text-gray-500">
                                            <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
                                                <span className="tw-flex tw-items-center tw-gap-1">
                                                    <Icon icon="mdi:account-outline" className="tw-text-gray-400" />
                                                    {authorName(n.createdBy)}
                                                </span>
                                                <span className="tw-flex tw-items-center tw-gap-1">
                                                    <Icon icon="mdi:clock-outline" className="tw-text-gray-400" />
                                                    {fmtDateTime(n.published_at)}
                                                </span>
                                                {Array.isArray(n.attachments) && n.attachments.length > 0 && (
                                                    <span className="tw-flex tw-items-center tw-gap-1">
                                                        <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                                                        {n.attachments.length} file{n.attachments.length === 1 ? '' : 's'}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="tw-flex tw-items-center tw-gap-2">
                                                {n.requires_acknowledgement && (
                                                    n.acknowledged_by_me ? (
                                                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-emerald-700 tw-font-medium">
                                                            <Icon icon="mdi:check-circle" className="tw-text-sm" /> Read
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => dispatch(acknowledgeNotice({ id, onSuccess: fetchBoard }))}
                                                            disabled={acknowledgeLoading}
                                                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-text-emerald-700 tw-bg-emerald-50 hover:tw-bg-emerald-100 disabled:tw-opacity-50"
                                                        >
                                                            <Icon icon="mdi:check" className="tw-text-sm" /> Mark as read
                                                        </button>
                                                    )
                                                )}
                                                <button
                                                    onClick={() => openNotice(id)}
                                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sky-700 hover:tw-text-sky-800 tw-font-medium"
                                                >
                                                    Read more <Icon icon="mdi:arrow-right" className="tw-text-sm" />
                                                </button>
                                            </span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}

                    {!noticeBoardLoading && ordered.length > 0 && totalPages > 1 && (
                        <div className="tw-pt-4 tw-mt-2 tw-border-t tw-border-gray-100">
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setPage}
                                showItemsPerPage={false}
                                itemLabel="notices"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NoticeBoard;
